import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import config from '../../config.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/AutoSuggest.css';

const LISTBOX_ID = 'requester-suggestions-listbox';

const RequesterAutoSuggest = ({
  id,
  value = '',
  onChange,
  onSelect,
  placeholder,
  className = '',
  disabled = false,
}) => {
  const { bandid } = useParams();
  const fetchWrapper = useFetchWrapper();
  const t = useT();
  const [viewers, setViewers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);

  // The listeners on the live show's watch page now (GET
  // /bands/{id}/shows/current/viewers, ENG-474): { showId, viewers: [{ id,
  // username }] }, with no viewers when the band is not live. Fetched on mount
  // and again on focus, since listeners come and go during a show.
  const fetchViewers = useCallback(async () => {
    if (!bandid) return;
    try {
      const data = await fetchWrapper.get(
        `${config.apiBaseUrl}/bands/${bandid}/shows/current/viewers`
      );
      if (Array.isArray(data)) {
        setViewers(data);
      } else if (data && Array.isArray(data.viewers)) {
        setViewers(data.viewers);
      }
    } catch {
      setViewers([]);
    }
  }, [fetchWrapper, bandid]);

  useEffect(() => {
    fetchViewers();
  }, [fetchViewers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.suggestions-dropdown')) {
        return;
      }
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterSuggestions = useCallback(
    (searchValue) => {
      if (!searchValue) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      const lower = searchValue.toLowerCase();
      const filtered = viewers
        .filter((v) => {
          const username = v.username || v.Username || '';
          return username.toLowerCase().includes(lower);
        })
        .slice(0, 10);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    },
    [viewers]
  );

  // Viewers that arrive after the member started typing (the fetch on focus
  // or mount had not answered yet) are matched against what is typed. Only
  // while nothing is shown, so a refetch never resets a highlighted option.
  useEffect(() => {
    if (value && !showSuggestions && inputRef.current === document.activeElement) {
      filterSuggestions(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on new viewers only
  }, [viewers]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange && onChange(newValue);
    filterSuggestions(newValue);
  };

  const handleSelect = (viewer) => {
    const username = viewer.username || viewer.Username || '';
    onSelect && onSelect(username);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleSelect(suggestions[selectedIndex]);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showSuggestions}
        aria-autocomplete="list"
        aria-controls={LISTBOX_ID}
        aria-activedescendant={
          selectedIndex >= 0 ? `requester-option-${selectedIndex}` : undefined
        }
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={fetchViewers}
        placeholder={placeholder ?? t('publicSongList.requester.placeholder')}
        className={className}
        disabled={disabled}
        autoComplete="off"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div id={LISTBOX_ID} className="suggestions-dropdown" role="listbox">
          {suggestions.map((viewer, index) => {
            const username = viewer.username || viewer.Username || '';
            const viewerKey = viewer.id || viewer.ID || username;
            return (
              <div
                key={viewerKey}
                id={`requester-option-${index}`}
                role="option"
                aria-selected={index === selectedIndex}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(viewer);
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="suggestion-title">{username}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RequesterAutoSuggest;
